import { useEffect, useMemo, useState } from 'react';
import { subscribeRolUsuario } from '../services/rolesUsuariosService';
import { resolveRolUsuario } from '../utils/rolesUsuario';
import { useComercialClientes } from './useComercialClientes';

/**
 * Rol del usuario interno logueado (ver resolveRolUsuario) más su doc del padrón
 * de comerciales del SGC, que también define el alcance de "Mis clientes".
 *
 * Se llama con el email solo para personal interno y con `null` en cualquier otro
 * caso. Si la lectura del rol asignado falla se sigue como "sin rol asignado":
 * nunca deja a alguien afuera por un error de lectura (las reglas siguen mandando).
 *
 * `isLoading` se deriva del email de la última respuesta: en el render en que
 * aparece el email todavía no hay respuesta para él, y no hay que resolver el rol
 * con datos de "sin usuario".
 *
 * @returns {{ rolUsuario: object|null, comercial: object|null, isLoading: boolean }}
 */
export const useRolUsuario = (email) => {
    const [asignado, setAsignado] = useState({ email: null, doc: null });
    const { comercial, isLoading: isComercialLoading } = useComercialClientes(email);

    useEffect(() => {
        if (!email) return undefined;
        return subscribeRolUsuario(
            email,
            (doc) => setAsignado({ email, doc }),
            () => setAsignado({ email, doc: null }),
        );
    }, [email]);

    const docAsignado = email && asignado.email === email ? asignado.doc : null;
    const isLoading = !!email && (asignado.email !== email || isComercialLoading);
    const rolUsuario = useMemo(
        () => (email && !isLoading
            ? resolveRolUsuario({ email, rolAsignado: docAsignado, comercialSgc: comercial })
            : null),
        [email, isLoading, docAsignado, comercial],
    );

    return { rolUsuario, comercial, isLoading };
};
